import { useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Icon } from "@/components/ui/Icon";
import { Pager, usePager } from "@/components/ui/Pager";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Toolbar } from "@/components/ui/Toolbar";
import { usePeople } from "@/lib/api/people";
import { useList } from "@/lib/api/queries";
import { dateTime, qty, relative, shortId } from "@/lib/format";
import "@/modules/reports/staff.css";

/** `GET /api/v1/journal` (inventory-service) yozuvi. */
interface Entry {
  id: string;
  source_kind: "MOVEMENT" | "LP" | "EXTERNAL";
  operation: string;
  occurred_at: string;
  actor_id?: string;
  device_id?: string;
  lp_id?: string;
  lp_code?: string;
  sku_id?: string;
  lot_ref?: string;
  qty?: string;
  quality_status?: string;
  from_location_id?: string;
  to_location_id?: string;
  from_holder_id?: string;
  to_holder_id?: string;
  from_status?: string;
  to_status?: string;
  task_id?: string;
  order_id?: string;
  reason?: string;
  details?: Record<string, unknown>;
}

interface User {
  id: string;
  username: string;
  full_name: string;
  is_active: boolean;
}

const PAGE_SIZE = 100;

/**
 * Amal turlari — odam tilida. Guruh filtrda ham ishlatiladi: «Pallet»
 * tanlansa barcha LP amallari bir so'rovda keladi.
 */
const OPERATIONS: Array<{ group: string; items: Array<[string, string]> }> = [
  {
    group: "Pallet",
    items: [
      ["LP_CREATED", "Pallet yaratildi"],
      ["LP_LIFTED", "Pallet ko'tarildi"],
      ["LP_LOWERED", "Pallet qo'yildi"],
      ["LP_MOVED", "Pallet ko'chdi"],
      ["LP_STATUS", "Pallet holati o'zgardi"],
      ["LP_UPDATED", "Pallet sektori"],
      ["LP_CLOSED", "Pallet yopildi"],
      ["TARA_BOUND", "Tara buyurtmaga bog'landi"],
    ],
  },
  {
    group: "Tovar harakati",
    items: [
      ["RECEIPT", "Qabul"],
      ["PUTAWAY", "Joylashtirish"],
      ["MOVE", "Ko'chirish"],
      ["PICK", "Terish"],
      ["REPLENISHMENT", "To'ldirish"],
      ["ADJUSTMENT", "Tuzatish (inventarizatsiya)"],
      ["QUALITY_CHANGE", "Sifat o'zgarishi"],
      ["SHIPMENT", "Jo'natish"],
      ["RETURN", "Qaytarish"],
      ["WRITEOFF", "Hisobdan chiqarish"],
      ["REVERSAL", "Bekor qilish"],
      ["OPENING_BALANCE", "Boshlang'ich qoldiq"],
      ["PRODUCTION_ISSUE", "Ishlab chiqarishga"],
      ["PRODUCTION_OUTPUT", "Ishlab chiqarishdan"],
    ],
  },
];

const OPERATION_LABEL = new Map(OPERATIONS.flatMap((group) => group.items));

function operationLabel(code: string): string {
  return OPERATION_LABEL.get(code) ?? code;
}

/** Guruh tanlovi: `group:Pallet` — guruhning barcha turlari. */
function operationQuery(value: string): string | undefined {
  if (!value) return undefined;
  if (value.startsWith("group:")) {
    const group = OPERATIONS.find((item) => item.group === value.slice("group:".length));
    return group?.items.map(([code]) => code).join(",");
  }
  return value;
}

/**
 * Jurnal — yacheyka va palletlar bilan bog'liq har bir ombor amali:
 * kim, qachon, qaysi LP, qayerdan → qayerga, nima va qancha, qaysi
 * topshiriq yoki buyurtma bo'yicha.
 *
 * Manba — inventory-service `warehouse_journal`: har harakat va LP
 * o'zgarishi o'z tranzaksiyasida yoziladi, tarix tizim boshidan
 * to'ldirilgan. Filtrlar SERVERDA: katak tarixida u yerga KIRGAN va
 * u yerdan CHIQQAN amallar birga ko'rinadi.
 */
export function Journal() {
  const [cell, setCell] = useState("");
  const [lp, setLp] = useState("");
  const [worker, setWorker] = useState("");
  const [operation, setOperation] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // Matn maydonlari Enter (yoki «Qidirish») bilan qo'llanadi: har harf
  // so'rov yubormaydi.
  const [applied, setApplied] = useState({ cell: "", lp: "" });

  const filters = [applied.cell, applied.lp, worker, operation, from, to].join("|");
  const pager = usePager(filters);
  const journal = useList<Entry>("inventory", "/api/v1/journal", {
    limit: PAGE_SIZE,
    cursor: pager.cursor,
    location_code: applied.cell || undefined,
    lp_code: applied.lp || undefined,
    actor_id: worker || undefined,
    operation: operationQuery(operation),
    from: from || undefined,
    to: to || undefined,
  });
  const staff = useList<User>("identity", "/api/v1/users", { limit: 500 });

  const rows = journal.data?.items ?? [];
  const people = usePeople(rows.flatMap((row) => [row.actor_id, row.from_holder_id, row.to_holder_id]));
  const locationIds = [...new Set(rows.flatMap((row) => [row.from_location_id, row.to_location_id]).filter(Boolean))].join(",");
  const skuIds = [...new Set(rows.map((row) => row.sku_id).filter(Boolean))].join(",");
  const locations = useList<{ id: string; code: string }>(
    "topology", "/api/v1/locations", { ids: locationIds, limit: 500 }, { enabled: locationIds !== "" });
  const skus = useList<{ id: string; article: string; name: string }>(
    "masterData", "/api/v1/skus", { ids: skuIds, limit: 500 }, { enabled: skuIds !== "" });
  const cellCode = new Map((locations.data?.items ?? []).map((item) => [item.id, item.code]));
  const skuOf = new Map((skus.data?.items ?? []).map((item) => [item.id, item]));

  const person = (id?: string) => (id ? people.get(id) ?? shortId(id) : "");
  const place = (location?: string, holder?: string) => {
    if (location) return <span className="mono">{cellCode.get(location) ?? shortId(location)}</span>;
    if (holder) return <span title="Xodim qo'lida">{person(holder)}</span>;
    return null;
  };

  const filtered = Boolean(applied.cell || applied.lp || worker || operation || from || to);
  const apply = () => setApplied({ cell: cell.trim().toUpperCase(), lp: lp.trim().toUpperCase() });

  const columns: Array<Column<Entry>> = [
    {
      key: "time",
      header: "Vaqt",
      value: (row) => row.occurred_at,
      render: (row) => <span title={dateTime(row.occurred_at)}>{relative(row.occurred_at)}</span>,
    },
    {
      key: "operation",
      header: "Amal",
      value: (row) => operationLabel(row.operation),
      render: (row) => <StatusBadge value={operationLabel(row.operation)} tone={row.source_kind === "MOVEMENT" ? "info" : "neutral"} />,
    },
    {
      key: "actor",
      header: "Kim",
      value: (row) => person(row.actor_id),
      render: (row) => (row.actor_id ? <span title={row.device_id ? `Qurilma: ${row.device_id}` : undefined}>{person(row.actor_id)}</span> : <span className="staff-muted">tizim</span>),
    },
    {
      key: "lp",
      header: "Pallet",
      value: (row) => row.lp_code ?? "",
      render: (row) =>
        row.lp_code ? (
          <button type="button" className="btn btn-text btn-sm mono" onClick={() => { setLp(row.lp_code!); setApplied((current) => ({ ...current, lp: row.lp_code! })); }}>
            {row.lp_code}
          </button>
        ) : null,
    },
    {
      key: "route",
      header: "Qayerdan → qayerga",
      render: (row) => {
        const source = place(row.from_location_id, row.from_holder_id);
        const target = place(row.to_location_id, row.to_holder_id);
        if (!source && !target) return <span className="staff-muted">—</span>;
        return (
          <span>
            {source ?? <span className="staff-muted">—</span>} → {target ?? <span className="staff-muted">—</span>}
          </span>
        );
      },
    },
    {
      key: "sku",
      header: "Tovar",
      render: (row) => {
        if (!row.sku_id) return null;
        const item = skuOf.get(row.sku_id);
        return item ? `${item.article} · ${item.name}` : <span className="mono">{shortId(row.sku_id)}</span>;
      },
    },
    {
      key: "qty",
      header: "Miqdor",
      align: "right",
      value: (row) => Number(row.qty ?? 0),
      render: (row) => (row.qty ? <span className="num">{qty(row.qty)}</span> : null),
    },
    {
      key: "status",
      header: "Holat",
      render: (row) =>
        row.to_status ? (
          <span>
            {row.from_status && row.from_status !== row.to_status && (
              <>
                <StatusBadge value={row.from_status} /> →{" "}
              </>
            )}
            <StatusBadge value={row.to_status} />
          </span>
        ) : row.quality_status && row.quality_status !== "AVAILABLE" ? (
          <StatusBadge value={row.quality_status} />
        ) : null,
    },
    {
      key: "reason",
      header: "Asos",
      render: (row) => (
        <span className="staff-name">
          {row.reason && <span>{row.reason}</span>}
          {(row.task_id || row.order_id) && (
            <span className="staff-muted mono">
              {row.task_id ? `topshiriq ${shortId(row.task_id)}` : ""}
              {row.task_id && row.order_id ? " · " : ""}
              {row.order_id ? `buyurtma ${shortId(row.order_id)}` : ""}
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Jurnal"
        hint="Yacheyka va palletlar bilan bog'liq har bir amal: kim, qachon, qaysi LP, qayerdan qayerga, nima va qancha. Katak yoki LP kodi bo'yicha — uning to'liq tarixi."
      />

      <Toolbar onRefresh={() => void journal.refetch()} refreshing={journal.isFetching}>
        <form
          className="toolbar"
          style={{ margin: 0, padding: 0, flexWrap: "wrap" }}
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <input className="input mono" style={{ width: 150 }} placeholder="Katak kodi" value={cell}
            onChange={(event) => setCell(event.target.value)} onBlur={apply} aria-label="Katak kodi" />
          <input className="input mono" style={{ width: 150 }} placeholder="LP kodi" value={lp}
            onChange={(event) => setLp(event.target.value)} onBlur={apply} aria-label="LP kodi" />
          <button type="submit" className="btn btn-tonal">
            <Icon name="search" size={16} />
            Qidirish
          </button>
        </form>
        <select className="input" value={worker} onChange={(event) => setWorker(event.target.value)} aria-label="Xodim">
          <option value="">Barcha xodimlar</option>
          {(staff.data?.items ?? []).map((user) => (
            <option key={user.id} value={user.id}>
              {user.full_name || user.username}
              {user.is_active ? "" : " (nofaol)"}
            </option>
          ))}
        </select>
        <select className="input" value={operation} onChange={(event) => setOperation(event.target.value)} aria-label="Amal turi">
          <option value="">Barcha amallar</option>
          {OPERATIONS.map((group) => (
            <optgroup key={group.group} label={group.group}>
              <option value={`group:${group.group}`}>{group.group} — hammasi</option>
              {group.items.map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <label className="staff-date">
          <span>Dan</span>
          <input className="input" type="date" value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label className="staff-date">
          <span>Gacha</span>
          <input className="input" type="date" value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} />
        </label>
        {filtered && (
          <button
            type="button"
            className="btn btn-text btn-sm"
            onClick={() => {
              setCell("");
              setLp("");
              setApplied({ cell: "", lp: "" });
              setWorker("");
              setOperation("");
              setFrom("");
              setTo("");
            }}
          >
            Tozalash
          </button>
        )}
      </Toolbar>

      <DataTable
        tableId="warehouse-journal"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={journal.isLoading}
        error={journal.error}
        onRetry={() => void journal.refetch()}
        emptyTitle={filtered ? "Bu filtr bo'yicha amal yo'q" : "Jurnal bo'sh"}
        emptyHint={
          applied.cell
            ? "Katak kodi to'g'ri yozilganini tekshiring (masalan A01-02-03-04)."
            : "Qabul, joylashtirish, terish va pallet amallari shu yerga tushadi."
        }
        footer={
          <Pager
            page={pager.page}
            atFirst={pager.atFirst}
            shown={rows.length}
            hasMore={journal.data?.has_more}
            onPrev={pager.prev}
            onNext={() => pager.next(journal.data?.next_cursor)}
          />
        }
      />
    </>
  );
}
